/**
 * Print header (Angular 18)
 *
 * The print header mode — the "Hide quantity for $0 items" checkbox, a Print
 * link and a Close link inside the shared header chrome. Composes
 * {@link HeaderShellComponent} directly via standalone imports.
 *
 * Migrated from apps/b2b-ui/common/directives/header/print.tpl.html.
 * Downgraded for AngularJS as `<ng-header-print>`.
 *
 * Uses `@Input()`/`@Output()` decorators (not signal `model()`/`output()`):
 * the hybrid app compiles with ts-loader + Angular JIT, which only discovers
 * decorator-declared inputs/outputs — signal APIs are invisible to it and fail
 * at runtime (NG0303). `hideItems` + `hideItemsChange` form the two-way binding.
 *
 * Re-wiring of the old `$scope` event bus:
 *  - `hideItems`/`hideItemsChange` replace `header.hideItems`, its
 *    `$scope.$watch`, and the `header::hideItems` emit. `hideItemsChange` is
 *    bound to `OrdersPrintController` in the print layout.
 *  - `print` replaces the `Header::print` emit. It is also published to
 *    {@link PrintService} so the far-up AngularJS `AppController` (out of
 *    binding range) is notified.
 */
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { NgIf } from '@angular/common';

import { AuthenticationService } from '../../../core/auth/authentication.service';
import { FeatureToggleService } from '../../../core/feature-toggle/feature-toggle.service';
import { ThemesHelperService } from '../../../core/themes-helper/themes-helper.service';
import { PrintService } from '../../../core/print/print.service';
import { HeaderShellComponent } from '../header-shell/header-shell.component';

@Component({
  selector: 'app-header-print',
  standalone: true,
  imports: [NgIf, HeaderShellComponent],
  template: require('./header-print.component.html'),
})
export class HeaderPrintComponent {
  /** Two-way bound to the "Hide quantity for $0 items" checkbox. */
  @Input() hideItems = false;
  @Output() hideItemsChange = new EventEmitter<boolean>();

  /** Emitted when the Print link is clicked. */
  @Output() print = new EventEmitter<void>();

  private readonly auth = inject(AuthenticationService);
  private readonly featureToggle = inject(FeatureToggleService);
  private readonly themesHelper = inject(ThemesHelperService);
  private readonly printService = inject(PrintService);

  private readonly user = this.auth.getUserFromCookie();

  /** Mirrors `user && (user.hasOwnProperty('email') || user.hasOwnProperty('userName'))`. */
  readonly isLoggedIn =
    !!this.user &&
    (Object.prototype.hasOwnProperty.call(this.user, 'email') ||
      Object.prototype.hasOwnProperty.call(this.user, 'userName'));

  readonly useCart = this.featureToggle.check('TENANT_CART');

  /** Mirrors `getCompany('name') || 'Tenant ID: ' + user.tenantId`. */
  readonly companyName: string =
    this.themesHelper.getCompany('name') || `Tenant ID: ${this.user?.tenantId ?? ''}`;

  /** Update the two-way `hideItems` binding from the checkbox. */
  onHideItemsToggle(event: Event): void {
    this.hideItems = (event.target as HTMLInputElement).checked;
    this.hideItemsChange.emit(this.hideItems);
  }

  /** Fire the `print` output and notify the AngularJS AppController via PrintService. */
  onPrint(): void {
    this.print.emit();
    this.printService.requestPrint();
  }

  /** Closes the print window (was `href="javascript:window.close()"`). */
  close(): void {
    window.close();
  }
}
